import { useState } from "react";
import { ChevronDown, CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { SERVICES } from "@/data/site";

const SelectField = ({ label, testid, value, onChange, children }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-gold/80">{label}</span>
    <div className="relative">
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none rounded-xl border border-gold/25 bg-ember px-4 py-3.5 pr-10 text-sm text-cream outline-none transition-colors duration-300 focus:border-gold/60"
      >
        {children}
      </select>
      <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-gold/70" />
    </div>
  </label>
);

export default function BookingForm({ presetService }) {
  const [serviceSlug, setServiceSlug] = useState(presetService ?? SERVICES[0].slug);
  const service = SERVICES.find((s) => s.slug === serviceSlug) ?? SERVICES[0];
  const [duration, setDuration] = useState(service.durations[0].mins);
  const [date, setDate] = useState(null);
  const [name, setName] = useState("");

  const onServiceChange = (slug) => {
    const next = SERVICES.find((s) => s.slug === slug) ?? SERVICES[0];
    setServiceSlug(next.slug);
    setDuration(next.durations[0].mins);
  };

  const submit = (e) => {
    e.preventDefault();
    const lines = [
      "Hello Belong Spa, I would like to book an appointment.",
      `Service: ${service.name}`,
      `Duration: ${duration} minutes`,
      `Preferred date: ${date ? format(date, "EEE, dd MMM yyyy") : "Flexible / any day"}`,
      name.trim() ? `Name: ${name.trim()}` : null,
    ].filter(Boolean);
    window.open(`https://wa.me/919866634473?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener");
  };

  return (
    <form data-testid="booking-form" onSubmit={submit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <SelectField label="Service" testid="booking-service-select" value={serviceSlug} onChange={onServiceChange}>
          {SERVICES.map((s) => (
            <option key={s.slug} value={s.slug}>{s.name}</option>
          ))}
        </SelectField>
        <SelectField label="Duration" testid="booking-duration-select" value={duration} onChange={(v) => setDuration(Number(v))}>
          {service.durations.map((d) => (
            <option key={d.mins} value={d.mins}>{d.mins} minutes</option>
          ))}
        </SelectField>
        <div className="block">
          <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-gold/80">Preferred date</span>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="booking-date-trigger"
                className={`flex w-full items-center gap-2.5 rounded-xl border border-gold/25 bg-ember px-4 py-3.5 text-sm outline-none transition-colors duration-300 focus:border-gold/60 ${date ? "text-cream" : "text-stone-500"}`}
              >
                <CalendarDays size={16} className="shrink-0 text-gold/70" />
                {date ? format(date, "EEE, dd MMM yyyy") : "Select a date"}
              </button>
            </PopoverTrigger>
            <PopoverContent data-testid="booking-date-calendar" className="w-auto border-gold/25 bg-coal p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={{ before: new Date() }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        <label className="block">
          <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-gold/80">Name (optional)</span>
          <input
            data-testid="booking-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full rounded-xl border border-gold/25 bg-ember px-4 py-3.5 text-sm text-cream outline-none transition-colors duration-300 placeholder:text-stone-500 focus:border-gold/60"
          />
        </label>
      </div>
      <button
        type="submit"
        data-testid="booking-submit-button"
        className="flex w-full items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-6 py-4 text-sm font-bold text-white shadow-lg shadow-emerald-600/25 transition-all duration-300 hover:bg-[#1fb857] active:scale-[0.98]"
      >
        <WhatsAppIcon size={18} />
        Send Booking on WhatsApp
      </button>
      <p className="text-center text-xs text-stone-500">
        No payment now — we confirm your slot on WhatsApp within minutes.
      </p>
    </form>
  );
}
